import posthog from "posthog-js";
import { useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useCookies } from "react-cookie";

import { PrescreenLayout } from "@/components/prescreen";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import CompanyInformationInfoCardContent from "./InformationInfoCardContent";
import { Loader2, ArrowRight, AlertTriangle } from "lucide-react";

import request from "@/lib/api/handler";
import { useSetUserPrescreenIntent } from "@/mutations/user/set-user-prescreen-intent";
import { useDraftProfileStore } from "@/stores/draft-profile";
import { REFERRAL_COOKIE_NAME } from "@/consts";

const formSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email().optional(),
  phoneNumber: z.string().transform((val, ctx) => {
    const number = val.replace(/\D/g, "");

    if (number.length !== 10) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_string,
        message: "Phone number must be 10 digits",
        validation: "regex",
      });

      return z.NEVER;
    }

    return number;
  }),
  companyName: z.string(),
  companyLocation: z.string().optional(),
  companyWebsiteLink: z.preprocess(
    (arg) => {
      if (typeof arg === "string" && arg === "") {
        return undefined;
      } else {
        return arg;
      }
    },
    z.string().regex(/^(http(s)?:\/\/)?www\..*$/, {
      message: 'A URL should start with "www."',
    }),
  ),
  marketingConsent: z.boolean().default(false),
});

type FormSchema = z.infer<typeof formSchema>;

const CompanyPrescreenTwo = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const [cookies] = useCookies([REFERRAL_COOKIE_NAME]);

  const { setUserPrescreenIntent } = useSetUserPrescreenIntent();
  const {
    setDraftProfileCompanyName,
    setDraftProfileSessionId,
    setDraftProfileEmail,
    setDraftProfileIntent,
  } = useDraftProfileStore();

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
  });

  const { isPending, mutate, error } = useMutation<string, Error, FormSchema>({
    mutationKey: ["pre-screen", "company", "your-information"],
    mutationFn: async (variables) => {
      return request({
        method: "POST",
        path: "/api/user/draft-profile",
        body: JSON.stringify({
          ...variables,
          name: `${variables.firstName} ${variables.lastName}`,
          intendedRole: "Company",
          referralCode: cookies[REFERRAL_COOKIE_NAME] as unknown,
        }),
      });
    },

    onSuccess: (result, { email, companyName }) => {
      setDraftProfileCompanyName(companyName ?? null);
      setDraftProfileSessionId(result);
      setDraftProfileEmail(email ?? null);
      setDraftProfileIntent("company");
      posthog.capture("company_user_basic_info");
      posthog.capture("company_basic_info");

      return navigate("/pre-screen/company/terms-of-service");
    },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setUserPrescreenIntent("Company"), []);

  const handleLogin = () =>
    void (async () => {
      await loginWithRedirect({
        appState: {
          returnTo: "/landing",
        },
      });
    })();

  const { marketingConsent } = form.watch();

  return (
    <PrescreenLayout
      title={"Let’s get to know each other"}
      subtitle={"We’ll build a company profile and attract on-demand talent."}
      step={1}
      stepTotal={3}
      form={
        <Form {...form}>
          <div className="flex-1 flex flex-col gap-4 justify-between self-stretch">
            <div className="flex flex-col gap-2">
              {error && (
                <>
                  <div className="w-full flex flex-row items-center gap-4 bg-card border rounded-md p-8">
                    <AlertTriangle />
                    {error.message}
                  </div>
                </>
              )}
              <form className="pt-4 pb-5 w-full grid grid-cols-2 gap-5">
                <FormField
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Company Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Acme, Inc." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="companyLocation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Location</FormLabel>
                      <FormControl>
                        <Input placeholder="New York, NY" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="companyWebsiteLink"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <FormLabel required>
                        Company Website or LinkedIn URL
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://www.linkedin.com/yourcompany"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
              <div className="w-full h-[1px] bg-[#E3E3E9]" />
              <form className="pt-4 pb-5 w-full grid grid-cols-2 gap-5">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Jane" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {!isAuthenticated && (
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>Email</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="john.smith@outlook.com"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Phone Number</FormLabel>
                      <FormControl>
                        <Input placeholder="123-456-7890" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
              <div
                className="w-fit text-sm text-[#2E2E38] underline cursor-pointer"
                onClick={() => navigate("/pre-screen/candidate/two")}
              >
                I&apos;m a candidate, not a company
              </div>
              {!isAuthenticated && (
                <div
                  className="w-fit text-sm text-[#2E2E38] underline cursor-pointer"
                  onClick={handleLogin}
                >
                  Already have an account? Sign in.
                </div>
              )}
            </div>
            <div className="flex flex-col gap-4">
              <div className="w-full flex flex-row gap-4">
                <Checkbox
                  checked={marketingConsent}
                  onCheckedChange={(state) => {
                    form.setValue(
                      "marketingConsent",
                      typeof state === "string" ? false : state,
                    );
                  }}
                />
                <p className="text-xs text-[#89899F] font-medium">
                  I agree to receive automated text messages and emails from
                  Venturous for marketing updates. Consent is a not a condition
                  of purchase. Reply HELP for help and STOP to unsubscribe. Msg
                  frequency varies. Msg and data rates may apply. Subject to{" "}
                  <a
                    href="https://venturous.work/terms-and-conditions"
                    className="text-inline-link hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://venturous.work/privacy-policy"
                    className="text-inline-link hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Notice
                  </a>
                  .
                </p>
              </div>
              <div className="w-full h-[1px] bg-border" />
              <div className="w-full flex flex-row justify-between">
                <Button
                  variant="stroke"
                  onClick={() => navigate("/pre-screen/")}
                >
                  Back
                </Button>
                <Button
                  variant="success"
                  disabled={!form.formState.isValid || isPending ? true : false}
                  onClick={() =>
                    void form.handleSubmit((data) => mutate(data))()
                  }
                >
                  Continue
                  {isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <ArrowRight />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Form>
      }
      infoCardContent={<CompanyInformationInfoCardContent />}
    />
  );
};

export default CompanyPrescreenTwo;
